"use client";

import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Icon, type IconName } from "../../icons";
import { Button } from "../Button/Button";
import "./PageHeader.css";

// ============================================
// Props
// ============================================
export interface PageHeaderProps {
  /** Breadcrumb text (displayed above title) */
  breadcrumb?: string;
  /** Page title */
  title?: string;
  /** Subtitle below the title */
  subtitle?: string;
  /** Action buttons */
  actions?: React.ReactNode;
  /** Children (for compound component usage) */
  children?: React.ReactNode;
  /** Additional CSS class */
  className?: string;
}

export interface PageHeaderTitleProps {
  children: React.ReactNode;
  className?: string;
}

export interface PageHeaderActionsProps {
  children: React.ReactNode;
  className?: string;
}

export interface PageHeaderSearchProps {
  children: React.ReactNode;
  className?: string;
}

export interface PageHeaderBreadcrumbProps {
  children: React.ReactNode;
  className?: string;
}

export interface PageHeaderSubtitleProps {
  children: React.ReactNode;
  className?: string;
}

// ============================================
// Subcomponents
// ============================================
const PageHeaderBreadcrumb: React.FC<PageHeaderBreadcrumbProps> = ({
  children,
  className = "",
}) => (
  <p className={`page-header__breadcrumb ${className}`.trim()}>{children}</p>
);

const PageHeaderTitle: React.FC<PageHeaderTitleProps> = ({
  children,
  className = "",
}) => <h1 className={`page-header__title ${className}`.trim()}>{children}</h1>;

const PageHeaderSubtitle: React.FC<PageHeaderSubtitleProps> = ({
  children,
  className = "",
}) => <p className={`page-header__subtitle ${className}`.trim()}>{children}</p>;

const PageHeaderActions: React.FC<PageHeaderActionsProps> = ({
  children,
  className = "",
}) => (
  <div className={`page-header__actions ${className}`.trim()}>{children}</div>
);

const PageHeaderSearch: React.FC<PageHeaderSearchProps> = ({
  children,
  className = "",
}) => (
  <div className={`page-header__search ${className}`.trim()}>{children}</div>
);

// ============================================
// Responsive Actions (mobile collapse)
// ============================================
const flattenChildren = (children: React.ReactNode): React.ReactNode[] => {
  const result: React.ReactNode[] = [];
  React.Children.forEach(children, (child) => {
    if (!child) return;
    if (React.isValidElement(child) && child.type === React.Fragment) {
      const fragmentProps = child.props as { children?: React.ReactNode };
      result.push(...flattenChildren(fragmentProps.children ?? []));
    } else {
      result.push(child);
    }
  });
  return result;
};

const ResponsiveActions: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const childArray = flattenChildren(children);
  const actionCount = childArray.length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDropdownOpen(false);
    };
    if (dropdownOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [dropdownOpen]);

  if (actionCount === 0) return null;

  return (
    <div className="page-header__actions-responsive">
      <div className="page-header__actions page-header__actions--desktop">
        {children}
      </div>

      <div className="page-header__actions page-header__actions--mobile">
        {actionCount === 1 ? (
          children
        ) : (
          <div className="page-header__actions-dropdown" ref={dropdownRef}>
            <Button
              variant="secondary"
              size="sm"
              trailingVisual="ChevronDown"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
            >
              Actions
            </Button>
            <AnimatePresence>
              {dropdownOpen && (
                <motion.div
                  className="page-header__actions-menu"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.12 }}
                  role="menu"
                >
                  {childArray.map((child, index) => {
                    type ActionElementProps = {
                      children?: React.ReactNode;
                      leadingVisual?: IconName | React.ReactNode;
                      onClick?: () => void;
                      href?: string;
                    };

                    const childElement = React.isValidElement(child)
                      ? (child as React.ReactElement<ActionElementProps>)
                      : null;
                    const childProps = childElement?.props;
                    const label = childProps?.children ?? `Action ${index + 1}`;
                    const icon = childProps?.leadingVisual;
                    const onClick = childProps?.onClick;
                    const href = childProps?.href;

                    const isLink =
                      childElement?.type?.toString()?.includes("Link") ||
                      childProps?.href !== undefined;
                    const buttonChild =
                      isLink && React.isValidElement(childProps?.children)
                        ? (childProps?.children as React.ReactElement<ActionElementProps>)
                        : null;
                    const buttonLabel = buttonChild?.props?.children ?? label;
                    const buttonIcon =
                      buttonChild?.props?.leadingVisual ?? icon;
                    const actualHref = href || childProps?.href;

                    const renderMenuIcon = (
                      value?: IconName | React.ReactNode,
                    ) => {
                      if (!value) return null;
                      if (typeof value === "string") {
                        return <Icon name={value as IconName} size={16} />;
                      }
                      return React.isValidElement(value) ? value : null;
                    };
                    const primaryMenuIcon = renderMenuIcon(buttonIcon);
                    const fallbackMenuIcon = renderMenuIcon(
                      buttonIcon || icon,
                    );

                    const handleClick = () => {
                      setDropdownOpen(false);
                      if (onClick) onClick();
                    };

                    if (actualHref) {
                      return (
                        <a
                          key={index}
                          href={actualHref}
                          className="page-header__actions-menu-item"
                          role="menuitem"
                          onClick={handleClick}
                        >
                          {primaryMenuIcon && (
                            <span className="page-header__actions-menu-icon">
                              <Icon
                                name={buttonIcon as IconName}
                                size={16}
                              />
                            </span>
                          )}
                          <span className="page-header__actions-menu-label">
                            {buttonLabel}
                          </span>
                        </a>
                      );
                    }

                    return (
                      <button
                        key={index}
                        className="page-header__actions-menu-item"
                        role="menuitem"
                        onClick={handleClick}
                      >
                        {fallbackMenuIcon && (
                          <span className="page-header__actions-menu-icon">
                            <Icon
                              name={(buttonIcon || icon) as IconName}
                              size={16}
                            />
                          </span>
                        )}
                        <span className="page-header__actions-menu-label">
                          {buttonLabel !== label ? buttonLabel : label}
                        </span>
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// Main Component
// ============================================
const PageHeaderInternal: React.FC<PageHeaderProps> = ({
  breadcrumb,
  title,
  subtitle,
  actions,
  children,
  className = "",
}) => {
  if (title || breadcrumb || subtitle || actions) {
    return (
      <div className={`page-header ${className}`.trim()}>
        <div className="page-header__left">
          {breadcrumb && (
            <>
              <PageHeaderBreadcrumb>{breadcrumb}</PageHeaderBreadcrumb>
              <span className="page-header__separator" aria-hidden="true" />
            </>
          )}
          {title && <PageHeaderTitle>{title}</PageHeaderTitle>}
          {subtitle && (
            <>
              <span className="page-header__separator" aria-hidden="true" />
              <PageHeaderSubtitle>{subtitle}</PageHeaderSubtitle>
            </>
          )}
        </div>
        {actions && <ResponsiveActions>{actions}</ResponsiveActions>}
      </div>
    );
  }

  return (
    <div className={`page-header ${className}`.trim()}>
      <div className="page-header__container">{children}</div>
    </div>
  );
};

// ============================================
// Export with subcomponents
// ============================================
export const PageHeader = Object.assign(PageHeaderInternal, {
  Breadcrumb: PageHeaderBreadcrumb,
  Title: PageHeaderTitle,
  Subtitle: PageHeaderSubtitle,
  Actions: PageHeaderActions,
  Search: PageHeaderSearch,
});
